import { APP_ROUTES } from '@/common/constants'
import { KYCStatus } from '@/common/types'
import { ACCOUNT_PARTICIPANT_ROLES } from '@/account/type'

export const KYC_FLOW_SCOPE = {
    ONBOARDING_USER: 'ONBOARDING_USER',
    APPLICATION_PARTICIPANT: 'APPLICATION_PARTICIPANT',
    APPLICATION_ACCOUNT: 'APPLICATION_ACCOUNT',
} as const

export type KycFlowScope = (typeof KYC_FLOW_SCOPE)[keyof typeof KYC_FLOW_SCOPE]

export const KYC_INIT_TARGET = {
    USER: 'USER',
    ACCOUNT_PARTICIPANT: 'ACCOUNT_PARTICIPANT',
    ACCOUNT: 'ACCOUNT',
} as const

export type KycInitTarget = (typeof KYC_INIT_TARGET)[keyof typeof KYC_INIT_TARGET]

export const APPLICATION_RESUME_VIEW = {
    PARTICIPANT: 'PARTICIPANT',
    ACCOUNT: 'ACCOUNT',
} as const

export type ApplicationResumeView = (typeof APPLICATION_RESUME_VIEW)[keyof typeof APPLICATION_RESUME_VIEW]

type ResolveParticipantIdParams = {
    kyc?: KYCStatus | null
    userId?: string | null
}

type ResolveKycInitTargetParams = ResolveParticipantIdParams & {
    pathname: string
}

export function resolveParticipantIdForKyc({ kyc, userId }: ResolveParticipantIdParams): string | null {
    const nonSelfParticipants = kyc?.participantsKyc?.filter((participant) => participant.userId !== userId) ?? []
    const primaryApplicant = nonSelfParticipants.find(
        (participant) => participant.role === ACCOUNT_PARTICIPANT_ROLES.PRIMARY_APPLICANT,
    )

    if (!nonSelfParticipants.length) {
        return null
    }

    return primaryApplicant?.userId ?? nonSelfParticipants[0].userId
}

export function resolveKycInitTarget({ pathname, kyc, userId }: ResolveKycInitTargetParams): {
    scope: KycFlowScope
    target: KycInitTarget
    participantId: string | null
} {
    const participantId = resolveParticipantIdForKyc({ kyc, userId })

    if (pathname !== APP_ROUTES.APPLICATION_KYC) {
        if (participantId) {
            return {
                scope: KYC_FLOW_SCOPE.APPLICATION_PARTICIPANT,
                target: KYC_INIT_TARGET.ACCOUNT_PARTICIPANT,
                participantId,
            }
        }

        return {
            scope: KYC_FLOW_SCOPE.APPLICATION_ACCOUNT,
            target: KYC_INIT_TARGET.ACCOUNT,
            participantId,
        }
    }

    // In application KYC, participant presence is the strongest signal.
    // Account type can be temporarily unavailable during account switches.
    if (participantId) {
        return {
            scope: KYC_FLOW_SCOPE.APPLICATION_PARTICIPANT,
            target: KYC_INIT_TARGET.ACCOUNT_PARTICIPANT,
            participantId,
        }
    }

    return {
        scope: KYC_FLOW_SCOPE.APPLICATION_ACCOUNT,
        target: KYC_INIT_TARGET.ACCOUNT,
        participantId,
    }
}

export function resolveApplicationResumeView({ kyc, userId }: { kyc?: KYCStatus | null; userId?: string | null }): {
    view: ApplicationResumeView
    participantId: string | null
} {
    const participantId = resolveParticipantIdForKyc({ kyc, userId })

    // Prefer participant resume whenever participant context is available.
    if (participantId) {
        return {
            view: APPLICATION_RESUME_VIEW.PARTICIPANT,
            participantId,
        }
    }

    return {
        view: APPLICATION_RESUME_VIEW.ACCOUNT,
        participantId,
    }
}
